import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Input,
	message,
	Modal,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	DeleteOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	SwapOutlined,
} from '@ant-design/icons';
import { deleteApi, getApi, postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import { loadFile, parseNumber } from '../parsers';
import { buildParsePayload } from '../buildParsePayload';
import type { ColumnMapping, WizardState, WizardAction } from '../wizardReducer';
import {
	applyAutoNegateDetection,
	autoSelectSoleField,
	buildAutoDetectedMappings,
	buildMappingsPayload,
	computeHeaderOverlapRatio,
	DEFAULT_MAPPING_MIN_HEADER_OVERLAP,
	deriveJsonId,
	getIncompleteColumnIndexes,
	getStep2ValidationError,
	parseTemplateEntries,
	resolveTemplateToMappings,
	suggestCategories,
	type MappingTemplate,
	type SectionDto,
} from './step2Helpers';
import {
	detectNonImportableColumn,
	getHiddenColumnReasons,
	NON_IMPORTABLE_REASON_LABELS,
} from './columnRelevance';
import styles from './Step2MapColumns.module.scss';


interface Step2MapColumnsProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	companyId: string;
	payPeriodId: string | null;
	onClose: () => void;
}

const FIELDS_URL = '/employee-cost/import/fields';
const TEMPLATES_URL = '/employee-cost/import/mapping-templates';
const CHECK_URL = '/employee-cost/import/fields/check-availability';
const SUGGEST_COLUMNS_URL = '/employee-cost/import/pdf/suggest-columns';
// Silent "last used" mapping per company — see SmartImportDefaultMapping on
// the backend and applyDefaultMappingIfGoodMatch below. Distinct from
// TEMPLATES_URL's named, user-curated template list.
const DEFAULT_MAPPING_URL = '/employee-cost/import/default-mapping';
const CREATE_NEW = '__create_new__';
// See the matching comment on Step3MapEmployees.tsx's TABLE_SCROLL_Y — sizing
// this off the Modal body's own 85vh cap (SmartImportWizard.tsx) keeps the
// table itself as the only visible scroll surface in the normal case,
// instead of nesting its fixed-height internal scrollbar inside the modal
// body's own fallback scrollbar. This step has more surrounding chrome above
// the table (the toolbar row of Suggest Categories/Load template/Save) than
// Step 3, hence the larger offset.
const TABLE_SCROLL_Y = 'max(240px, calc(85vh - 360px))';

const isSentinelCategory = (c: string) => c === 'employee_name' || c === 'skip';

// Color-code the selected Category value (UI concern; kept out of step2Helpers).
// Section coloring is by case-insensitive name substring — never by hardcoded ID —
// so it tracks whatever sections the company has configured.
function categoryColor(category: string, sections: SectionDto[]): string {
	if (category === 'employee_name') return '#1677ff';
	if (category === 'skip') return '#d9d9d9';
	const name = (sections.find((s) => s.id === category)?.name ?? '').toLowerCase();
	if (name.includes('salary')) return '#52c41a';
	if (name.includes('fringe') || name.includes('payroll taxes')) return '#faad14';
	return '#8c8c8c';
}

function categoryLabel(category: string, sections: SectionDto[]): string {
	if (category === 'employee_name') return 'Employee Name';
	if (category === 'skip') return 'Skip';
	return sections.find((s) => s.id === category)?.name ?? category;
}

/**
 * Step 2 — Map Columns (WS-9). Builds the column-mapping table from the parsed
 * file, loads the section/field tree and saved templates, and hosts the inline
 * "+ Add New Field" flow. Owns server snapshots and inline-UI state locally; the
 * user's mapping *decisions* live in the wizard reducer.
 */
export default function Step2MapColumns({
	state,
	dispatch,
	companyId,
	payPeriodId,
	onClose,
}: Step2MapColumnsProps) {
	// ENG-407 (R4): companyId is a prop sourced once from the wizard (like Steps 3/4),
	// NOT re-read from localStorage on every render. localStorage.companyId can be
	// rewritten mid-session (companySlice.getCompanies), which previously let the
	// post-save refresh GET fetch a different company than the save POST wrote to —
	// so a freshly-saved template never appeared in the Load dropdown.

	const [sections, setSections] = useState<SectionDto[]>([]);
	const [templates, setTemplates] = useState<MappingTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	// Inline "+ Add New Field" UI state — keyed by colIndex (sourceHeader can repeat).
	const [addingFieldFor, setAddingFieldFor] = useState<number | null>(null);
	const [newFieldLabel, setNewFieldLabel] = useState('');
	// Which row's Map To Field dropdown is open — controlled so the pinned
	// "+ Add New Field" header can close the menu after firing (mirrors Step 3's
	// "Add as new employee" pinned header).
	const [openFieldRow, setOpenFieldRow] = useState<number | null>(null);

	// Template Save As dialog state.
	const [saveAsOpen, setSaveAsOpen] = useState(false);
	const [saveAsName, setSaveAsName] = useState('');

	// Columns (by colIndex) that a loaded template could not resolve — flagged for remap.
	const [unresolvedCols, setUnresolvedCols] = useState<Set<number>>(new Set());

	// Warning when a loaded template's column count differs from this file's (Fix 8).
	const [columnCountWarning, setColumnCountWarning] = useState<string | null>(null);

	// ENG-383: true while re-parsing after a sheet switch (disables the switcher).
	const [isSwitching, setIsSwitching] = useState(false);

	// PDF extraction summary banner — collapsed by default (a one-line summary)
	// so a multi-document import doesn't eat modal space on entry; expands to
	// the full per-document breakdown (billing period, excluded pages,
	// adjustment rows, warnings) on demand.
	const [pdfSummaryExpanded, setPdfSummaryExpanded] = useState(false);

	// AI-assisted column suggestion (PDF imports only) — a follow-up to the
	// local keyword suggester (step2Helpers.ts's KEYWORD_INTENTS), which is a
	// small fixed English vocabulary that mostly misses carrier-specific PDF
	// invoice column names. High-confidence suggestions are applied directly
	// (see fetchAiColumnSuggestions); low-confidence ones are never
	// auto-applied — held here and rendered as a clickable hint next to the
	// still-"Skip" column instead, so an uncertain guess is visible without
	// being silently committed to.
	const [aiColumnSuggestions, setAiColumnSuggestions] = useState<
		Map<number, { sectionId: string; sectionName: string; reason?: string }>
	>(new Map());

	// ENG-372: shared template-apply core, used by both the manual picker
	// (handleApplyTemplate) and the auto-re-apply on file/sheet change. Resolves a
	// template's entries against a supplied base set of column mappings and dispatches
	// the result. `sectionsArg`/`templatesArg` are passed in (not read from state) so
	// callers can use freshly-loaded values before React has committed them to state.
	//
	// With `requireHeaderOverlap`, this is the deferred half of the Option-B logic:
	// the reducer preserves loadedTemplateId across FILE_SELECTED / SHEET_SWITCHED, and
	// here — once the new headers are known — we either re-apply the template (the new
	// file shares columns) or clear loadedTemplateId (it doesn't, or the template was
	// deleted between sessions).
	const applyTemplateById = (
		templateId: string,
		baseMappings: ColumnMapping[],
		sectionsArg: SectionDto[],
		templatesArg: MappingTemplate[],
		options: { requireHeaderOverlap?: boolean } = {}
	): void => {
		const template = templatesArg.find((t) => t.id === templateId);
		if (!template) {
			// Auto-re-apply only: the loaded template was deleted between sessions —
			// clear the phantom selection so the picker doesn't show a missing template.
			// (The manual picker path can't reach this — its id comes from this list.)
			if (options.requireHeaderOverlap) {
				dispatch({ type: 'TEMPLATE_LOADED_ID_SET', loadedTemplateId: null });
				setColumnCountWarning(null);
				setUnresolvedCols(new Set());
			}
			return;
		}
		const entries = parseTemplateEntries(template.mappings);

		// ENG-372 (Option B): on auto-re-apply, only re-apply when the new file/sheet
		// shares at least one source column with the template. If nothing overlaps the
		// file is unrelated — clear the selection so the user starts fresh.
		if (options.requireHeaderOverlap) {
			const incomingHeaders = new Set(baseMappings.map((m) => m.sourceHeader));
			const overlaps = entries.some((e) => incomingHeaders.has(e.sourceHeader));
			if (!overlaps) {
				dispatch({ type: 'TEMPLATE_LOADED_ID_SET', loadedTemplateId: null });
				setColumnCountWarning(null);
				setUnresolvedCols(new Set());
				return;
			}
		}

		const { mappings, unresolved, pendingFieldsToCreate, outOfRange } =
			resolveTemplateToMappings(baseMappings, entries, sectionsArg);
		setUnresolvedCols(new Set(unresolved));

		// Warn only when template entries genuinely couldn't be applied — the file
		// layout may have changed (R2-6). No counts in the message.
		if (unresolved.length > 0 || outOfRange > 0) {
			setColumnCountWarning(
				"Some template mappings couldn't be applied — the file layout may have changed. Review and re-save the template to update it."
			);
		} else {
			setColumnCountWarning(null);
		}

		dispatch({
			type: 'TEMPLATE_APPLIED',
			columnMappings: mappings,
			loadedTemplateId: template.id,
		});
		// Reconstruct any pending fields the template carried but the DB doesn't have
		// yet (Fix 6). Must run AFTER TEMPLATE_APPLIED so PENDING_FIELD_ADDED updates
		// the freshly-applied mapping rows to the right tempId.
		for (const pf of pendingFieldsToCreate) {
			dispatch({
				type: 'PENDING_FIELD_ADDED',
				colIndex: pf.colIndex,
				pendingField: {
					tempId: pf.tempId,
					label: pf.label,
					sectionId: pf.sectionId,
					derivedJsonId: pf.derivedJsonId,
				},
			});
		}
		if (unresolved.length > 0) {
			toastText(
				`${unresolved.length} column(s) could not be matched and need remapping.`,
				'info'
			);
		}
	};

	// Silent "last used" mapping for this company (SmartImportDefaultMapping on
	// the backend) — applied only when there's no explicit named template to
	// re-apply (that always wins, see the call site) AND the new file's headers
	// meaningfully overlap what was saved (DEFAULT_MAPPING_MIN_HEADER_OVERLAP) —
	// otherwise this silently does nothing and the normal keyword auto-detect
	// (already seeded into columnMappings before this runs) stands as-is.
	// Deliberately does NOT touch loadedTemplateId/setColumnCountWarning — this
	// isn't a template, so "Load template..." must not show it selected.
	const applyDefaultMappingIfGoodMatch = (
		baseMappings: ColumnMapping[],
		sectionsArg: SectionDto[],
		defaultEntries: ReturnType<typeof parseTemplateEntries>
	): void => {
		if (defaultEntries.length === 0) return;
		const headers = baseMappings.map((m) => m.sourceHeader);
		if (
			computeHeaderOverlapRatio(headers, defaultEntries) <
			DEFAULT_MAPPING_MIN_HEADER_OVERLAP
		) {
			return;
		}
		const { mappings, pendingFieldsToCreate } = resolveTemplateToMappings(
			baseMappings,
			defaultEntries,
			sectionsArg
		);
		dispatch({ type: 'DEFAULT_MAPPING_APPLIED', columnMappings: mappings });
		for (const pf of pendingFieldsToCreate) {
			dispatch({
				type: 'PENDING_FIELD_ADDED',
				colIndex: pf.colIndex,
				pendingField: {
					tempId: pf.tempId,
					label: pf.label,
					sectionId: pf.sectionId,
					derivedJsonId: pf.derivedJsonId,
				},
			});
		}
	};

	// PDF imports only — see aiColumnSuggestions' declaration comment above.
	// `baseMappings`/`sectionsArg` are passed in (not read from state) so this
	// can be called with freshly-computed values before React commits them,
	// same pattern as applyTemplateById above.
	const fetchAiColumnSuggestions = async (
		baseMappings: ColumnMapping[],
		sectionsArg: SectionDto[]
	) => {
		if (!state.pdfExtraction || sectionsArg.length === 0) return;
		// IDs/dates/codes are hidden from File Column (columnRelevance.ts) — don't
		// spend an AI call asking whether e.g. "Member ID Number" is a cost.
		const unresolved = baseMappings.filter(
			(m) =>
				m.category === 'skip' &&
				m.classifier === 'numeric' &&
				!detectNonImportableColumn(m)
		);
		if (unresolved.length === 0) return;

		try {
			const res = await postApi(SUGGEST_COLUMNS_URL, {
				companyId,
				payPeriodId,
				columns: unresolved.map((m) => ({
					colIndex: m.colIndex,
					header: m.sourceHeader,
					samples: m.sampleValues,
				})),
				sections: sectionsArg.map((s) => ({ id: s.id, name: s.name })),
			});
			const suggestions: Array<{
				colIndex: number;
				sectionId: string;
				confidence: 'high' | 'low';
				reason?: string;
			}> = res.data?.suggestions ?? [];
			if (suggestions.length === 0) return;

			const lowMap = new Map<
				number,
				{ sectionId: string; sectionName: string; reason?: string }
			>();
			const highByCol = new Map<number, string>();
			for (const s of suggestions) {
				if (s.confidence === 'high') {
					highByCol.set(s.colIndex, s.sectionId);
				} else {
					const sectionName =
						sectionsArg.find((sec) => sec.id === s.sectionId)?.name ?? s.sectionId;
					lowMap.set(s.colIndex, { sectionId: s.sectionId, sectionName, reason: s.reason });
				}
			}
			setAiColumnSuggestions(lowMap);

			if (highByCol.size > 0) {
				let updated = baseMappings.map((m) =>
					highByCol.has(m.colIndex)
						? { ...m, category: highByCol.get(m.colIndex)!, fieldRef: { type: 'none' as const } }
						: m
				);
				updated = autoSelectSoleField(updated, sectionsArg);
				dispatch({ type: 'CATEGORIES_SUGGESTED', columnMappings: updated });
			}
		} catch {
			// Best-effort follow-up — the local keyword suggestions and manual
			// picker are completely unaffected by a failure here.
		}
	};

	// Load the section/field tree and saved templates; auto-detect on first entry.
	useEffect(() => {
		if (!companyId || !payPeriodId) {
			setLoading(false);
			setLoadError('Missing company or pay period context.');
			return;
		}
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setLoadError(null);
			try {
				const [fieldsRes, templatesRes, defaultMappingRes] = await Promise.all([
					getApi(FIELDS_URL, { companyId, payPeriodId }),
					getApi(TEMPLATES_URL, { companyId }),
					// Best-effort: a missing/failed default-mapping fetch just means no
					// silent auto-apply this time, not a Step 2 load failure.
					getApi(DEFAULT_MAPPING_URL, { companyId }).catch(() => null),
				]);
				if (cancelled) return;
				// Use the freshly-loaded sections (the `sections` state set below is not
				// yet visible within this effect run) for keyword pre-selection (ENG-378).
				const loadedSections: SectionDto[] = fieldsRes.data?.sections ?? [];
				setSections(loadedSections);
				// NOTE: listTemplates returns a bare array, not { templates: [] } as
				// in Tech Spec §4.1 — adapt to the actual implementation (prep O4).
				const loadedTemplates: MappingTemplate[] = Array.isArray(
					templatesRes.data
				)
					? templatesRes.data
					: [];
				setTemplates(loadedTemplates);
				if (state.columnMappings.length === 0) {
					const headers = (state.rawMatrix[state.headerRowIndex] ?? []).map(
						String
					);
					// ENG-408 always seeded categories at Skip and left suggestion to an
					// explicit "Suggest Categories" click. Now seeds with the same
					// keyword-suggested categories (and, where a suggested section has
					// exactly one field, its field) by default — the user still reviews
					// and can change every cell, same as the chat-driven flow already does.
					let autoMappings = buildAutoDetectedMappings(
						headers,
						state.rawMatrix,
						state.headerRowIndex
					);
					autoMappings = suggestCategories(autoMappings, loadedSections);
					autoMappings = autoSelectSoleField(autoMappings, loadedSections);
					// Not for PDF invoices: an all-negative column there is a printed
					// credit (e.g. BCBS "Adjustment Amount" -$946.44) and must import
					// exactly as printed. Flipping it would turn credits into charges.
					// The user can still toggle Convert by hand.
					if (!state.pdfExtraction) {
						autoMappings = applyAutoNegateDetection(autoMappings);
					}
					dispatch({
						type: 'STEP2_INITIALIZED',
						columnMappings: autoMappings,
					});
					void fetchAiColumnSuggestions(autoMappings, loadedSections);
					// ENG-372 (Option B): if a template was loaded before the file changed,
					// re-apply it against the freshly auto-detected base so returning to
					// Step 2 with a new file restores the mapping automatically (the ticket's
					// specified behavior). Runs AFTER STEP2_INITIALIZED so it resolves against
					// the seeded base mappings; uses the freshly-loaded sections/templates
					// because the corresponding state isn't committed yet. applyTemplateById
					// clears loadedTemplateId when the new file shares no columns with the
					// template. Guarded by `columnMappings.length === 0` so a plain Back→Next
					// (no file change, mappings preserved) never clobbers the user's edits.
					if (state.loadedTemplateId) {
						applyTemplateById(
							state.loadedTemplateId,
							autoMappings,
							loadedSections,
							loadedTemplates,
							{ requireHeaderOverlap: true }
						);
					} else {
						// No explicit named template to re-apply — offer the company's
						// silent "last used" mapping instead, if there is one and it
						// looks like a real match for this file (see the function's
						// own header comment for the precedence/overlap reasoning).
						const defaultEntries = parseTemplateEntries(
							defaultMappingRes?.data?.mappings
						);
						applyDefaultMappingIfGoodMatch(
							autoMappings,
							loadedSections,
							defaultEntries
						);
					}
				}
			} catch {
				if (!cancelled)
					setLoadError('Could not load fields and templates. Please try again.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
		// One-shot per pay period; the reducer guards against clobbering edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payPeriodId]);

	const refreshTemplates = async () => {
		if (!companyId) return;
		try {
			const res = await getApi(TEMPLATES_URL, { companyId });
			setTemplates(Array.isArray(res.data) ? res.data : []);
		} catch {
			/* non-fatal: keep the current list */
		}
	};

	// ENG-383: re-parse the file on a different sheet and rebuild the Step 2 column
	// mapping. Because the Step 2 init effect is one-shot per pay period, we seed the
	// auto-detected mappings here (via STEP2_INITIALIZED) after SHEET_SWITCHED has
	// cleared the prior sheet's mappings. Any existing mapping work is confirmed away
	// first, and name-column auto-detection re-runs for the new sheet.
	const handleSheetSwitch = async (newSheetName: string) => {
		if (!state.file || newSheetName === state.selectedSheet) return;
		const previousSheet = state.selectedSheet;

		if (state.columnMappings.length > 0) {
			const confirmed = await new Promise<boolean>((resolve) => {
				Modal.confirm({
					title: 'Switch sheet?',
					content: 'Switching sheets will reset your column mappings. Continue?',
					okText: 'Switch sheet',
					cancelText: 'Cancel',
					onOk: () => resolve(true),
					onCancel: () => resolve(false),
				});
			});
			if (!confirmed) return;
		}

		setIsSwitching(true);
		dispatch({ type: 'SHEET_SWITCHED', sheet: newSheetName });
		try {
			const detected = await loadFile(state.file, newSheetName);
			dispatch({ type: 'PARSE_SUCCEEDED', payload: buildParsePayload(detected) });
			const headers = (detected.rawRows[detected.headerRowIndex] ?? []).map(
				String
			);
			let autoMappings = buildAutoDetectedMappings(
				headers,
				detected.rawRows,
				detected.headerRowIndex
			);
			autoMappings = suggestCategories(autoMappings, sections);
			autoMappings = autoSelectSoleField(autoMappings, sections);
			autoMappings = applyAutoNegateDetection(autoMappings);
			dispatch({
				type: 'STEP2_INITIALIZED',
				columnMappings: autoMappings,
			});
			// ENG-372 (Option B): re-apply a loaded template against the switched sheet's
			// columns (or clear it when the new sheet shares none). Mirrors the file-change
			// re-apply in the mount effect; runs after STEP2_INITIALIZED so it resolves
			// against the seeded base. sections/templates are already in component state by
			// the time the switcher is usable. state.loadedTemplateId is the pre-switch id
			// (SHEET_SWITCHED preserves it).
			if (state.loadedTemplateId) {
				applyTemplateById(
					state.loadedTemplateId,
					autoMappings,
					sections,
					templates,
					{ requireHeaderOverlap: true }
				);
			}
		} catch {
			// Re-parse failed (empty/unreadable sheet). Restore the prior sheet's
			// label so the banner isn't left pointing at a sheet we couldn't load,
			// and surface a non-blocking error. State stays consistent — the user
			// can retry. (SHEET_SWITCHED re-runs the reset, but the prior sheet's
			// mappings were already discarded when the switch began.)
			if (previousSheet) {
				dispatch({ type: 'SHEET_SWITCHED', sheet: previousSheet });
			}
			message.error('Failed to load sheet — please try again.');
		} finally {
			setIsSwitching(false);
		}
	};

	const sectionsByNo = useMemo(
		() => [...sections].sort((a, b) => a.no - b.no),
		[sections]
	);

	const closeInlineInput = () => {
		setAddingFieldFor(null);
		setNewFieldLabel('');
	};

	// ---- Field-select value + change ---------------------------------------
	const fieldSelectValue = (m: ColumnMapping): string | undefined => {
		if (m.fieldRef.type === 'existing') return m.fieldRef.fieldId;
		if (m.fieldRef.type === 'pending') return m.fieldRef.tempId;
		return undefined;
	};

	const handleFieldChange = (record: ColumnMapping, value: string) => {
		if (value === CREATE_NEW) {
			setAddingFieldFor(record.colIndex);
			// ENG-387: pre-populate the inline input with the source column header so
			// the user can accept, edit, or clear it. Sliced to the input's maxLength
			// (50) so the seeded value never exceeds what the field allows.
			setNewFieldLabel(record.sourceHeader.slice(0, 50));
			return;
		}
		dispatch({
			type: 'COLUMN_FIELD_CHANGED',
			colIndex: record.colIndex,
			fieldRef: value.startsWith('__pending__')
				? { type: 'pending', tempId: value }
				: { type: 'existing', fieldId: value },
		});
	};

	// ---- Inline field creation (§9) ----------------------------------------
	const handleConfirmNewField = async (record: ColumnMapping) => {
		const label = newFieldLabel.trim();
		if (label === '') return;
		const sectionId = record.category; // guaranteed a real section (sentinel hides the option)
		const sectionName =
			sections.find((s) => s.id === sectionId)?.name ?? 'this section';

		// 1. Same-section dedup against the wizard's own pending fields.
		const dupPending = state.pendingFields.find(
			(pf) =>
				pf.sectionId === sectionId &&
				pf.label.trim().toLowerCase() === label.toLowerCase()
		);
		if (dupPending) {
			Modal.confirm({
				title: <span style={{ fontSize: 16 }}>Field already added</span>,
				content: (
					<span style={{ fontSize: 12 }}>
						You already added a new field called &ldquo;{dupPending.label}
						&rdquo; to {sectionName}. Use it?
					</span>
				),
				okText: 'Use Existing',
				cancelText: 'Cancel',
				onOk: () => {
					dispatch({
						type: 'COLUMN_FIELD_CHANGED',
						colIndex: record.colIndex,
						fieldRef: { type: 'pending', tempId: dupPending.tempId },
					});
					closeInlineInput();
				},
			});
			return;
		}

		// 2. Soft availability check against persisted fields.
		try {
			const res = await getApi(CHECK_URL, {
				companyId,
				sectionId,
				name: label,
				payPeriodId,
			});
			if (res.data?.available === false && res.data?.existingField) {
				const existing = res.data.existingField as { id: string; name: string };
				Modal.confirm({
					title: <span style={{ fontSize: 16 }}>Field already exists</span>,
					content: (
						<span style={{ fontSize: 12 }}>
							A field called &ldquo;{existing.name}&rdquo; already exists in{' '}
							{sectionName}.
						</span>
					),
					okText: 'Use Existing',
					cancelText: 'Cancel',
					onOk: () => {
						dispatch({
							type: 'COLUMN_FIELD_CHANGED',
							colIndex: record.colIndex,
							fieldRef: { type: 'existing', fieldId: existing.id },
						});
						closeInlineInput();
					},
				});
				return;
			}
		} catch {
			toastText('Could not check field availability. Please try again.', 'error');
			return;
		}

		// 3. Create the pending field and map the column to it.
		dispatch({
			type: 'PENDING_FIELD_ADDED',
			colIndex: record.colIndex,
			pendingField: {
				tempId: `__pending__${state.pendingFields.length + 1}`,
				label,
				sectionId,
				derivedJsonId: deriveJsonId(label),
			},
		});
		closeInlineInput();
	};

	// ---- Templates (§10) ----------------------------------------------------
	// The user explicitly picked a template — apply it against the current mappings.
	// No header-overlap gate here (that guards the auto-re-apply path only); a
	// deliberate pick always applies, with unresolved columns flagged for remap.
	const handleApplyTemplate = (templateId: string) => {
		applyTemplateById(templateId, state.columnMappings, sections, templates);
	};

	const handleDeleteTemplate = async (template: MappingTemplate) => {
		try {
			await deleteApi(`${TEMPLATES_URL}/${template.id}`);
			// If the deleted template was the currently loaded one, clear it.
			if (state.loadedTemplateId === template.id) {
				dispatch({ type: 'TEMPLATE_LOADED_ID_SET', loadedTemplateId: null });
				setColumnCountWarning(null);
				setUnresolvedCols(new Set());
			}
			await refreshTemplates();
			toastText('Template deleted.', 'success');
		} catch {
			toastText('Could not delete the template. Please try again.', 'error');
		}
	};

	const confirmDeleteTemplate = (template: MappingTemplate) => {
		Modal.confirm({
			title: 'Delete template?',
			content: `"${template.name}" will be permanently deleted.`,
			okText: 'Delete',
			okButtonProps: { danger: true },
			onOk: () => handleDeleteTemplate(template),
		});
	};

	// ENG-408: on-demand keyword-based category suggestion for all columns.
	const handleSuggestCategories = () => {
		Modal.confirm({
			title: 'Suggest categories?',
			content:
				'This will replace your current category selections with suggestions. Continue?',
			okText: 'Suggest',
			onOk: () => {
				let suggested = suggestCategories(state.columnMappings, sections);
				suggested = autoSelectSoleField(suggested, sections);
				dispatch({ type: 'CATEGORIES_SUGGESTED', columnMappings: suggested });
				void fetchAiColumnSuggestions(suggested, sections);
			},
		});
	};

	const currentTemplateName =
		templates.find((t) => t.id === state.loadedTemplateId)?.name ?? '';

	const handleSave = async () => {
		if (!state.loadedTemplateId) {
			// Nothing loaded → Save behaves as Save As (Functional Spec §9.4).
			setSaveAsName('');
			setSaveAsOpen(true);
			return;
		}
		try {
			await postApi(TEMPLATES_URL, {
				id: state.loadedTemplateId,
				companyId,
				name: currentTemplateName,
				mappings: buildMappingsPayload(
					state.columnMappings,
					sections,
					state.pendingFields
				),
			});
			toastText('Template updated.', 'success');
			await refreshTemplates();
		} catch {
			toastText('Could not save the template. Please try again.', 'error');
		}
	};

	const handleSaveAs = async () => {
		const name = saveAsName.trim();
		if (name === '') return;
		try {
			const res = await postApi(TEMPLATES_URL, {
				companyId,
				name,
				mappings: buildMappingsPayload(
					state.columnMappings,
					sections,
					state.pendingFields
				),
			});
			// Save endpoint returns the bare saved row.
			if (res.data?.id) {
				dispatch({ type: 'TEMPLATE_LOADED_ID_SET', loadedTemplateId: res.data.id });
			}
			toastText('Template saved.', 'success');
			setSaveAsOpen(false);
			setSaveAsName('');
			await refreshTemplates();
		} catch {
			toastText('Could not save the template. Please try again.', 'error');
		}
	};

	// Snapshots the current mappings (same shape Save/Save As already build) onto
	// wizard state before leaving Step 2, so Step 4 can silently POST it as this
	// company's new default mapping after a successful Confirm Import — without
	// Step 4 needing its own copy of `sections` to resolve field ids to names.
	const handleNext = () => {
		dispatch({
			type: 'MAPPINGS_PAYLOAD_FOR_SAVE_SET',
			payload: buildMappingsPayload(
				state.columnMappings,
				sections,
				state.pendingFields
			),
		});
		dispatch({ type: 'ADVANCE_STEP' });
	};

	// ---- Validation / counts ------------------------------------------------
	const validationError = getStep2ValidationError(state.columnMappings);
	const canContinue = !loading && !loadError && validationError === null;

	// Columns with a category chosen but no field mapped — lets the "N column(s)
	// have a category selected but no field mapped" message scroll/highlight the
	// exact rows instead of just naming a count. `.endsWith` pins this to that
	// specific message: getStep2ValidationError checks Employee Name and "at
	// least one cost field" first, so validationError can be non-null while this
	// list is non-empty for an unrelated reason (e.g. missing Employee Name) —
	// the suffix match keeps the click behavior scoped to the message it matches.
	const incompleteColIndexes = useMemo(
		() => getIncompleteColumnIndexes(state.columnMappings),
		[state.columnMappings]
	);
	const isIncompleteFieldWarning =
		!!validationError && incompleteColIndexes.length > 0 && validationError.endsWith('no field mapped.');

	const [highlightedCols, setHighlightedCols] = useState<Set<number>>(new Set());

	const scrollToIncompleteRows = () => {
		if (incompleteColIndexes.length === 0) return;
		document
			.getElementById(`step2-row-${incompleteColIndexes[0]}`)
			?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setHighlightedCols(new Set(incompleteColIndexes));
		window.setTimeout(() => setHighlightedCols(new Set()), 1600);
	};

	const costColumnCount = state.columnMappings.filter(
		(m) => !isSentinelCategory(m.category) && m.fieldRef.type !== 'none'
	).length;

	// Columns still at Skip that can never be a cost field (IDs, dates, codes,
	// descriptions — see columnRelevance.ts) are left out of the table so the
	// user only sees columns worth deciding about. Display-only: they stay in
	// state.columnMappings as Skip, so templates, Steps 3-4 and the import are
	// unchanged, and choosing a category for one (after "Show") keeps it visible.
	const [showHiddenColumns, setShowHiddenColumns] = useState(false);
	const hiddenColumnReasons = useMemo(
		() => getHiddenColumnReasons(state.columnMappings),
		[state.columnMappings]
	);
	const visibleColumnMappings = useMemo(
		() =>
			showHiddenColumns
				? state.columnMappings
				: state.columnMappings.filter((m) => !hiddenColumnReasons.has(m.colIndex)),
		[showHiddenColumns, state.columnMappings, hiddenColumnReasons]
	);
	const hiddenReasonSummary = Array.from(new Set(hiddenColumnReasons.values()))
		.map((r) => NON_IMPORTABLE_REASON_LABELS[r])
		.join(', ');

	// ---- Table columns ------------------------------------------------------
	const columns: ColumnsType<ColumnMapping> = [
		{
			// ENG-393 (regression fix): give File Column an explicit width. The table
			// runs table-layout:fixed (scroll.y, no scroll.x), so the only column
			// WITHOUT a width absorbs `container − sum(fixed widths)`. When that
			// remainder shrank toward zero the header wrapped one letter per line. A
			// fixed width keeps the header on one line regardless of container size.
			title: 'File Column',
			dataIndex: 'sourceHeader',
			key: 'sourceHeader',
			width: 260,
			render: (header: string, record) => {
				const hiddenReason = hiddenColumnReasons.get(record.colIndex);
				return (
					<>
						<strong>{header}</strong>
						{hiddenReason && (
							<Tag style={{ marginLeft: 6, fontSize: 11 }}>
								{NON_IMPORTABLE_REASON_LABELS[hiddenReason]}
							</Tag>
						)}
					</>
				);
			},
		},
		{
			title: 'Sample',
			key: 'sample',
			width: 180,
			render: (_: unknown, record) => {
				const samples = record.sampleValues.slice(0, 2);
				// Display-only sign flip so the user sees the effect of Convert;
				// rawMatrix / sampleValues are never mutated. Uses the shared
				// parseNumber (not a local parseFloat) so accounting-bracket values
				// like "(150.00)" preview correctly, not just plain signed ones —
				// otherwise a bracket-format column with Convert auto-enabled would
				// show a stale, unflipped preview here.
				const displayed =
					record.negate && record.classifier === 'numeric'
						? samples.map((v) => {
								const n = parseNumber(v);
								return Number.isFinite(n) ? String(-n) : v;
						  })
						: samples;
				return (
					<span className={styles['step2__sample']}>
						{displayed.join(', ')}
					</span>
				);
			},
		},
		{
			title: 'Category',
			key: 'category',
			// ENG-393: back to 240 (original). The Tag's maxWidth:100% + ellipsis +
			// Tooltip handle long labels ("Payroll Taxes & Fringe Benefits"), so the
			// column doesn't need the extra 40px that squeezed File Column.
			width: 240,
			render: (_: unknown, record) => {
				const aiSuggestion =
					record.category === 'skip' ? aiColumnSuggestions.get(record.colIndex) : undefined;
				return (
					<div>
						<Select
							style={{ width: '100%' }}
							value={record.category}
							labelRender={({ value }) => {
								const color = categoryColor(String(value), sections);
								const label = categoryLabel(String(value), sections);
								const textColor = value === 'skip' ? '#595959' : '#fff';
								return (
									<Tooltip title={label}>
										<Tag
											color={color}
											style={{
												margin: 0,
												color: textColor,
												fontWeight: 500,
												maxWidth: '100%',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												display: 'inline-block',
												verticalAlign: 'middle',
											}}
										>
											{label}
										</Tag>
									</Tooltip>
								);
							}}
							onChange={(value: string) =>
								dispatch({
									type: 'COLUMN_CATEGORY_CHANGED',
									colIndex: record.colIndex,
									category: value,
								})
							}
						>
							<Select.Option value="employee_name">Employee Name</Select.Option>
							{sectionsByNo.map((section) => (
								<Select.Option key={section.id} value={section.id}>
									{section.name}
								</Select.Option>
							))}
							<Select.Option value="skip">Skip</Select.Option>
						</Select>
						{aiSuggestion && (
							<Tooltip
								title={
									aiSuggestion.reason
										? `AI guess, not confident: ${aiSuggestion.reason}`
										: "AI guess, not confident — click to apply"
								}
							>
								<Tag
									color="purple"
									style={{ marginTop: 4, cursor: 'pointer', fontSize: 11, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
									onClick={() =>
										dispatch({
											type: 'COLUMN_CATEGORY_CHANGED',
											colIndex: record.colIndex,
											category: aiSuggestion.sectionId,
										})
									}
								>
									AI suggests: {aiSuggestion.sectionName}
								</Tag>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			title: 'Map To Field',
			key: 'field',
			width: 280,
			render: (_: unknown, record) => {
				if (isSentinelCategory(record.category)) {
					return <span className={styles['step2__muted']}>—</span>;
				}
				const section = sections.find((s) => s.id === record.category);
				const sectionPending = state.pendingFields.filter(
					(pf) => pf.sectionId === record.category
				);
				const showRemap =
					unresolvedCols.has(record.colIndex) &&
					record.fieldRef.type === 'none';
				return (
					<div className={styles['step2__field-cell']}>
						<Select
							style={{ width: '100%' }}
							placeholder="Select a field"
							value={fieldSelectValue(record)}
							onChange={(value: string) => handleFieldChange(record, value)}
							status={showRemap ? 'warning' : ''}
							open={openFieldRow === record.colIndex}
							onOpenChange={(open) =>
								setOpenFieldRow(open ? record.colIndex : null)
							}
							// "+ Add New Field" is pinned as a sticky header above the
							// scrollable field list so it stays visible regardless of scroll
							// position (mirrors Step 3's "Add as new employee" pattern).
							dropdownRender={(menu) => (
								<>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
											padding: '8px 12px',
											cursor: 'pointer',
											fontWeight: 500,
											color: '#1677ff',
											borderBottom: '1px solid #f0f0f0',
										}}
										// Let onClick run the action; prevent the mousedown from
										// stealing focus / re-toggling the dropdown.
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => {
											handleFieldChange(record, CREATE_NEW);
											setOpenFieldRow(null);
										}}
									>
										+ Add New Field
									</div>
									{menu}
								</>
							)}
						>
							{(section?.fields ?? [])
								.filter((f) => !f.isTotal)
								.map((f) => (
									<Select.Option key={f.id} value={f.id}>
										{f.name}
									</Select.Option>
								))}
							{sectionPending.map((pf) => (
								<Select.Option key={pf.tempId} value={pf.tempId}>
									{pf.label} <Tag color="green">new</Tag>
								</Select.Option>
							))}
						</Select>

						{showRemap && (
							<Tag color="warning" className={styles['step2__remap-tag']}>
								Needs remapping
							</Tag>
						)}

						{addingFieldFor === record.colIndex && (
							<div className={styles['step2__inline-input']}>
								<Input
									autoFocus
									size="small"
									maxLength={50}
									placeholder="New field name"
									value={newFieldLabel}
									onChange={(e) => setNewFieldLabel(e.target.value)}
									onPressEnter={() => void handleConfirmNewField(record)}
								/>
								<Button
									size="small"
									type="primary"
									disabled={newFieldLabel.trim() === ''}
									onClick={() => void handleConfirmNewField(record)}
								>
									✓
								</Button>
								<Button size="small" onClick={closeInlineInput}>
									✗
								</Button>
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: (
				<span className={styles['step2__convert-header']}>
					Convert
					{/* Click, not hover — this table sits inside a Modal, and the app's
					    global .ant-tooltip z-index (index.css) is tied with .ant-modal-mask's,
					    so a hover popup can lose the coin flip and render invisibly behind
					    it. Click-triggered avoids the hover-timing question entirely, and
					    zIndex here forces the same override .ant-select-dropdown/
					    .ant-picker-dropdown already use to win against that modal. Wrapped in
					    a real <button> (not a bare icon) so it's keyboard/AT accessible now
					    that it's interactive, not just decorative. */}
					<Tooltip
						title="Convert amount sign — flips the column's values from positive to negative (or negative to positive) on import."
						trigger="click"
						zIndex={99999}
						getPopupContainer={() => document.body}
					>
						<button type="button" className={styles['step2__info-btn']} aria-label="What does Convert do?">
							<InfoCircleOutlined aria-hidden="true" className={styles['step2__info-icon']} />
						</button>
					</Tooltip>
				</span>
			),
			key: 'negate',
			width: 110,
			align: 'center',
			render: (_: unknown, record) => {
				if (isSentinelCategory(record.category)) return null;
				// Only numeric (dollar) columns carry sign-flippable numbers.
				const canNegate = record.classifier === 'numeric';
				const active = record.negate && canNegate;
				return (
					<Tooltip
						title={canNegate ? 'Convert +/- signs' : 'Only numeric columns can be converted'}
						zIndex={99999}
						getPopupContainer={() => document.body}
					>
						<Button
							size="small"
							disabled={!canNegate}
							type={active ? 'primary' : 'default'}
							onClick={() =>
								dispatch({
									type: 'COLUMN_NEGATE_TOGGLED',
									colIndex: record.colIndex,
								})
							}
						>
							<SwapOutlined />
						</Button>
					</Tooltip>
				);
			},
		},
	];

	if (loadError) {
		return (
			<div className={styles['step2']}>
				<Alert type="error" showIcon message="Could not load Step 2" description={loadError} />
				<div className={styles['step2__footer']}>
					<Space>
						<Button className={styles['step2__btn-secondary']} onClick={() => dispatch({ type: 'BACK_STEP' })}>
							Previous
						</Button>
						<Button className={styles['step2__btn-secondary']} onClick={onClose}>
							Cancel
						</Button>
					</Space>
				</div>
			</div>
		);
	}

	const handleChangeFile = () => {
		// ENG-372 (Option B): don't clear loadedTemplateId here — preserve it so
		// returning to Step 2 re-applies the template when the new file shares
		// columns (or clears it automatically when it doesn't).
		setColumnCountWarning(null);
		dispatch({ type: 'BACK_STEP' });
	};

	return (
		<div className={styles['step2']}>
			{/* Items 1 & 2: a single compact line (title + file/row/column summary), with
			    Change File riding in Alert's own `action` slot right after the text —
			    instead of the old title+description two-tier Alert plus a separate
			    button row underneath, which cost an extra full row of vertical space. */}
			<Alert
				className={styles['step2__banner']}
				type="info"
				showIcon
				message={
					<span className={styles['step2__banner-title']}>
						Map your file&apos;s columns to CAP fields
						<span className={styles['step2__banner-desc']}>
							{' '}
							— {state.file?.name}
							{/* ENG-383: the sheet name shows here only for single-sheet files;
							    multi-sheet files get the switcher in the context bar below. */}
							{state.sheets.length <= 1 && state.selectedSheet
								? ` · sheet “${state.selectedSheet}”`
								: ''}{' '}
							· <span className={styles['step2__banner-num']}>{state.rawRows.length}</span> data rows ·{' '}
							<span className={styles['step2__banner-num']}>{costColumnCount}</span> cost column(s) mapped
						</span>
					</span>
				}
				action={
					<Button size="small" onClick={handleChangeFile}>
						Change File
					</Button>
				}
			/>

			{columnCountWarning && (
				<Alert
					type="warning"
					showIcon
					message={columnCountWarning}
					className={styles['step2__col-count-warning']}
				/>
			)}

			{/* Silent "last used" mapping (SmartImportDefaultMapping) applied — see
			    applyDefaultMappingIfGoodMatch. Distinct from columnCountWarning above:
			    this isn't a problem, just transparency for a mapping the user never
			    explicitly chose, since it drives real financial-column decisions. */}
			{state.defaultMappingApplied && (
				<Alert
					type="info"
					showIcon
					message="Applied your last mapping for this company — review before continuing."
					className={styles['step2__col-count-warning']}
				/>
			)}

			{/* PDF extraction summary — one line per uploaded carrier invoice (a
			    single-PDF upload still has exactly one entry in `documents`, see
			    joinAcrossDocuments.ts on the backend). Collapsed by default (a
			    one-line summary + "Show details"); expands to the full
			    per-document breakdown so a multi-document import's details are
			    all reachable without permanently eating modal space. */}
			{state.pdfExtraction && (() => {
				const docs = state.pdfExtraction.documents;
				const totalWarnings = docs.reduce((sum, d) => sum + d.warnings.length, 0);
				const docLabel = (doc: (typeof docs)[number], i: number) =>
					doc.carrierName || doc.benefitType || doc.documentType || `Document ${i + 1}`;
				return (
					<Alert
						type="info"
						showIcon
						className={styles['step2__col-count-warning']}
						message={
							<span>
								{`PDF extraction summary (${docs.length} document${docs.length === 1 ? '' : 's'})`}
								{!pdfSummaryExpanded && (
									<span className={styles['step2__banner-desc']}>
										{' — '}
										{docs.map(docLabel).join(', ')}
										{totalWarnings > 0
											? ` · ${totalWarnings} warning${totalWarnings === 1 ? '' : 's'}`
											: ''}
									</span>
								)}
							</span>
						}
						action={
							<Button
								size="small"
								type="link"
								onClick={() => setPdfSummaryExpanded((v) => !v)}
							>
								{pdfSummaryExpanded ? 'Hide details' : 'Show details'}
							</Button>
						}
						description={
							pdfSummaryExpanded ? (
								<div>
									{docs.map((doc, i) => {
										const adjustmentRowCount = doc.adjustmentContext.reduce((sum, t) => sum + t.rows.length, 0);
										const label = docLabel(doc, i);
										const period =
											doc.billingPeriod?.start || doc.billingPeriod?.end
												? `billing period ${doc.billingPeriod.start ?? '?'} – ${doc.billingPeriod.end ?? '?'}`
												: null;
										return (
											<div key={i} style={{ marginBottom: i === docs.length - 1 ? 0 : 8 }}>
												<strong>{label}</strong>
												{doc.documentType && doc.documentType !== label ? ` (${doc.documentType})` : ''}
												{period ? ` · ${period}` : ''}
												{doc.excludedPages.length > 0 ? ` · ${doc.excludedPages.length} page(s) excluded` : ''}
												{adjustmentRowCount > 0
													? ` · ${adjustmentRowCount} adjustment row(s) found but not imported (see the amount/reconciliation before confirming)`
													: ''}
												{doc.warnings.length > 0 && (
													<ul style={{ margin: '4px 0 0 0' }}>
														{doc.warnings.map((w, wi) => (
															<li key={wi}>{w}</li>
														))}
													</ul>
												)}
											</div>
										);
									})}
								</div>
							) : undefined
						}
					/>
				);
			})()}

			<div className={styles['step2__context-bar']}>
				<div className={styles['step2__context-left']}>
					{/* ENG-383: sheet switcher for multi-sheet Excel files; single-sheet
					    files show nothing. Placed here (not the banner) to keep the
					    detection banner uncluttered. */}
					{state.sheets.length > 1 && state.selectedSheet && (
						<>
							<span className={styles['step2__sheet-label']}>· Sheet:</span>
							<Select
								value={state.selectedSheet}
								onChange={handleSheetSwitch}
								loading={isSwitching}
								disabled={isSwitching || loading}
								size="small"
								style={{ minWidth: 160 }}
								options={state.sheets.map((s) => ({
									value: s.name,
									label: s.name,
								}))}
								popupMatchSelectWidth={false}
							/>
						</>
					)}
				</div>
				<div className={styles['step2__context-right']}>
					<Button onClick={handleSuggestCategories}>Suggest Categories</Button>
					<Select
						className={styles['step2__template-select']}
						placeholder="Load template…"
						value={state.loadedTemplateId ?? undefined}
						onChange={handleApplyTemplate}
						allowClear
						suffixIcon={state.loadedTemplateId ? null : undefined}
						onClear={() => {
							dispatch({ type: 'TEMPLATE_LOADED_ID_SET', loadedTemplateId: null });
							setColumnCountWarning(null);
							setUnresolvedCols(new Set());
						}}
						options={templates.map((t) => ({ value: t.id, label: t.name }))}
						optionRender={(option) => {
							const template = templates.find((t) => t.id === option.value);
							return (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 8,
									}}
								>
									<span
										style={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										{option.label}
									</span>
									{template && (
										<DeleteOutlined
											style={{ color: '#ff4d4f', fontSize: 12 }}
											onClick={(e) => {
												e.stopPropagation();
												confirmDeleteTemplate(template);
											}}
										/>
									)}
								</div>
							);
						}}
						notFoundContent="No saved templates"
						style={{ minWidth: 220 }}
					/>
					<Button onClick={() => void handleSave()}>Save</Button>
				</div>
			</div>

			{/* ENG-409: the split-name Combine / Use-as-is toggle was removed. All
			    columns mapped to Employee Name are now always combined on import. */}

			{hiddenColumnReasons.size > 0 && (
				<div className={styles['step2__hidden-note']}>
					{showHiddenColumns
						? `Showing ${hiddenColumnReasons.size} column(s) not needed for import (${hiddenReasonSummary}) — they stay on Skip.`
						: `${hiddenColumnReasons.size} column(s) hidden — not needed for import (${hiddenReasonSummary}).`}{' '}
					<Button
						type="link"
						size="small"
						className={styles['step2__hidden-toggle']}
						onClick={() => setShowHiddenColumns((v) => !v)}
					>
						{showHiddenColumns ? 'Hide them' : 'Show them'}
					</Button>
				</div>
			)}

			<Spin spinning={loading}>
				<Table<ColumnMapping>
					className={styles['step2__table']}
					columns={columns}
					dataSource={visibleColumnMappings}
					rowKey="colIndex"
					pagination={false}
					size="small"
					scroll={{ y: TABLE_SCROLL_Y, x: 'max-content' }}
					onRow={(record) => ({ id: `step2-row-${record.colIndex}` })}
					rowClassName={(record) =>
						highlightedCols.has(record.colIndex) ? styles['step2__row-highlight'] : ''
					}
				/>
			</Spin>

			{validationError && (
				<div
					className={`${styles['step2__validation']} ${
						isIncompleteFieldWarning ? styles['step2__validation--clickable'] : ''
					}`}
					{...(isIncompleteFieldWarning
						? {
								role: 'button',
								tabIndex: 0,
								onClick: scrollToIncompleteRows,
								onKeyDown: (e: React.KeyboardEvent) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										scrollToIncompleteRows();
									}
								},
						  }
						: {})}
				>
					<ExclamationCircleOutlined aria-hidden="true" />
					<span>{validationError}</span>
				</div>
			)}

			<div className={styles['step2__footer']}>
				<Space>
					<Button className={styles['step2__btn-secondary']} onClick={() => dispatch({ type: 'BACK_STEP' })}>
						Previous
					</Button>
					<Button className={styles['step2__btn-secondary']} onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="primary"
						disabled={!canContinue}
						onClick={handleNext}
						className="btn-black"
						style={{ height: '41px' }}
					>
						Next
					</Button>
				</Space>
			</div>

			<Modal
				open={saveAsOpen}
				title="Save mapping template"
				okText="Save"
				okButtonProps={{ disabled: saveAsName.trim() === '' }}
				onOk={() => void handleSaveAs()}
				onCancel={() => setSaveAsOpen(false)}
				destroyOnHidden
			>
				<Input
					autoFocus
					maxLength={100}
					placeholder="Template name"
					value={saveAsName}
					onChange={(e) => setSaveAsName(e.target.value)}
					onPressEnter={() => void handleSaveAs()}
				/>
			</Modal>
		</div>
	);
}
